import { USER_BACKEND_URL } from '../config/config';
import api from './api';

export interface Lead {
  _id?: string;
  first_name: string;
  last_name: string;
  email_address: string;
  job_title: string;
  company_name: string;
  management_level: string;
  mobile_phone: string;
  direct_phone_number: string;
  company_hq_phone: string;
  linkedin_url: string;
  [key: string]: any;
}

export interface LeadListResponse {
  success: boolean;
  data: Lead[];
  record_count: number;
}

export interface LeadCategory {
  _id: string;
  name: string;
  [key: string]: any;
}

export interface LeadCategoryListResponse {
  success: boolean;
  data: LeadCategory[];
  record_count: number;
}

class LeadService {
  /**
   * Upload leads via CSV file with a category.
   * is_new=true tells the backend to create the category before attaching it.
   */
  async uploadLeads(file: File, category: string, isNew: boolean): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('csv_file', file);
      formData.append('category', category);
      formData.append('is_new', String(isNew));
      const response = await api.post(`${USER_BACKEND_URL}file/uploadLeads`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading leads:', error);
      throw error;
    }
  }

  /**
   * Get lead list with pagination and search
   */
  async getLeadList(payload: {
    startToken: number;
    endToken: number;
    search: string;
  }): Promise<LeadListResponse> {
    try {
      const response = await api.post<LeadListResponse>(`${USER_BACKEND_URL}lead/leadList`, payload);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead list:', error);
      throw error;
    }
  }

  /**
   * Delete a single lead
   */
  async deleteLead(id: string): Promise<any> {
    try {
      const response = await api.post(`${USER_BACKEND_URL}lead/deleteLead`, { id });
      return response.data;
    } catch (error) {
      console.error('Error deleting lead:', error);
      throw error;
    }
  }

  /**
   * Get lead categories with server-side search + pagination.
   */
  async getLeadCategories(params: {
    search?: string;
    start?: number;
    limit?: number;
  }): Promise<LeadCategoryListResponse> {
    try {
      const response = await api.get<LeadCategoryListResponse>(
        `${USER_BACKEND_URL}category/list`,
        {
          params: {
            search: params.search ?? '',
            start: params.start ?? 0,
            limit: params.limit ?? 100,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching lead categories:', error);
      throw error;
    }
  }

  /**
   * Delete multiple leads
   */
  async deleteMultipleLeads(ids: string[]): Promise<any> {
    try {
      const response = await api.post(`${USER_BACKEND_URL}lead/deleteMultipleLeads`, { ids });
      return response.data;
    } catch (error) {
      console.error('Error deleting multiple leads:', error);
      throw error;
    }
  }
}

export default new LeadService();
